// src/services/noteService.ts
// Domain-specific service for note operations

import { apiClient } from "./api";
import { API_ENDPOINTS } from "../constants/index";
import { cache, CACHE_KEYS, withCache } from "../utils/cacheUtils";
import type { Note } from "../types/entities";
import { logger } from "@common/utils/logger";

// Type definitions
export type { Note };

/** `NoteBase` (POST /notes), less the owner the service adds. */
export interface CreateNoteData {
  title: string;
  body?: string | null;
  group_id?: number | null;
}

/** `NotePatch` (PATCH /notes/{id}): only the fields to change. */
export interface UpdateNoteData {
  title?: string;
  body?: string | null;
  group_id?: number | null;
}

export interface NoteSearchFilters {
  tags?: string[];
  created_after?: string;
  created_before?: string;
  owner_email?: string;
  is_public?: boolean;
}

export interface GroupNoteAssociation {
  groupId: number;
  noteId: number;
}

/**
 * Service for note operations
 */
export class NoteService {
  /**
   * Get all notes (with caching)
   */
  async getNotes(onFreshData?: (data: Note[]) => void): Promise<Note[]> {
    return withCache(
      CACHE_KEYS.NOTES,
      async () => {
        return await apiClient.get<Note[]>(API_ENDPOINTS.NOTES);
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        onCacheHit: () => logger.debug("[NoteService] Using cached notes"),
        onCacheMiss: () => logger.debug("[NoteService] Fetching fresh notes"),
        onFreshData,
      },
    );
  }

  /**
   * Get notes by group (with caching)
   */
  async getNotesByGroup(
    groupId: number,
    onFreshData?: (data: Note[]) => void,
  ): Promise<Note[]> {
    return withCache(
      CACHE_KEYS.GROUP_NOTES(groupId),
      async () => {
        return await apiClient.get<Note[]>(
          `${API_ENDPOINTS.GROUPS}${groupId}/notes`,
        );
      },
      {
        ttl: 5 * 60 * 1000, // 5 minutes cache
        onCacheHit: () =>
          logger.debug(`[NoteService] Using cached notes for group ${groupId}`),
        onCacheMiss: () =>
          logger.debug(
            `[NoteService] Fetching fresh notes for group ${groupId}`,
          ),
        onFreshData,
      },
    );
  }

  /**
   * Get a specific note
   */
  async getNote(noteId: number): Promise<Note> {
    return await apiClient.get<Note>(`${API_ENDPOINTS.NOTES}${noteId}`);
  }

  /**
   * Create a new note
   */
  async createNote(noteData: CreateNoteData, userEmail: string): Promise<Note> {
    // Remove group_id from the data - linking happens separately
    const { group_id, ...cleanNoteData } = noteData;

    const processedData = {
      ...cleanNoteData,
      owner_email: userEmail,
    };

    const newNote = await apiClient.post<Note>(
      API_ENDPOINTS.NOTES,
      processedData,
    );

    // Invalidate cache after creating
    cache.remove(CACHE_KEYS.NOTES);
    // Also invalidate groups cache since notes appear in note groups
    cache.remove(CACHE_KEYS.GROUPS);
    cache.remove(CACHE_KEYS.GROUPS + "_detailed");

    return newNote;
  }

  /**
   * Update an existing note
   */
  async updateNote(noteId: number, noteData: UpdateNoteData): Promise<Note> {
    const updatedNote = await apiClient.patch<Note>(
      `${API_ENDPOINTS.NOTES}${noteId}`,
      noteData,
    );

    // Invalidate cache after updating
    cache.remove(CACHE_KEYS.NOTES);
    // Also invalidate groups cache since notes appear in note groups
    cache.remove(CACHE_KEYS.GROUPS);
    cache.remove(CACHE_KEYS.GROUPS + "_detailed");

    return updatedNote;
  }

  /**
   * Delete a note
   */
  async deleteNote(noteId: number): Promise<number> {
    await apiClient.delete(`${API_ENDPOINTS.NOTES}${noteId}`);

    // Invalidate cache after deleting
    cache.remove(CACHE_KEYS.NOTES);
    // Also invalidate groups cache since notes appear in note groups
    cache.remove(CACHE_KEYS.GROUPS);
    cache.remove(CACHE_KEYS.GROUPS + "_detailed");

    return noteId;
  }

  /**
   * Link a note to a group
   */
  async linkToGroup(
    groupId: number,
    noteId: number,
  ): Promise<GroupNoteAssociation> {
    await apiClient.post(`${API_ENDPOINTS.GROUPS}${groupId}/notes/${noteId}`);
    return { groupId, noteId };
  }

  /**
   * Unlink a note from a group
   */
  async unlinkFromGroup(
    groupId: number,
    noteId: number,
  ): Promise<GroupNoteAssociation> {
    await apiClient.delete(`${API_ENDPOINTS.GROUPS}${groupId}/notes/${noteId}`);
    return { groupId, noteId };
  }

  /**
   * Search notes
   */
  async searchNotes(
    query: string,
    filters: NoteSearchFilters = {},
  ): Promise<Note[]> {
    const params = { q: query, ...filters };
    return await apiClient.get<Note[]>(API_ENDPOINTS.NOTES, params);
  }
}

// Create singleton instance
export const noteService = new NoteService();
